import { useEffect } from 'react';
import ReactHeap from 'reactjs-heap';

import { useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';

import { filterIdToRoadClass } from 'features/map/tooltips/RoadClassTooltip/utils';

import {
  kDefaultMapStyle,
  selectMapStyle,
  selectProject,
  selectRoadFilters,
} from '../../state/workflowSlice';

class HeapAnalyticsService {
  userProperties: { project_slug?: string; domain?: string } = {};
  userId?: string = undefined;

  get heap() {
    // @ts-ignore Property 'heap' does not exist on type 'Window & typeof globalThis'.
    return window.heap;
  }

  initialize() {
    if (this.isInitialized) return;
    ReactHeap.initialize(2183439397);
    this.userProperties.domain = `${window.location.hostname}:${window.location.port}`;
    this.addUserProperties();
  }

  get isInitialized() {
    return !!this.heap?.appid;
  }

  setUserIdentify(userId: string) {
    if (!this.isInitialized) return;
    if (userId !== this.userId && userId) {
      // console.log('Identify ', userId);
      this.heap.identify(
        `${userId}/${window.location.hostname}:${window.location.port}`,
      );
    }
  }

  resetUserIdentify() {
    if (!this.isInitialized) return;

    this.heap.resetIdentity();
    this.heap.clearEventProperties();
  }

  addUserProperties() {
    if (!this.isInitialized) return;
    // console.log('Writing properties to heap ', this.userProperties);
    this.heap.addUserProperties(this.userProperties);
  }

  setProjectProperty(project_slug: string) {
    this.userProperties.project_slug = project_slug;
    this.addUserProperties();
  }

  setMapStyleEventProperty(mapStyle: string) {
    if (!this.isInitialized) return;
    this.heap.addEventProperties({ basemap_style: mapStyle });
  }

  setRoadClassesEventProperty(roadFilters: string[] | null) {
    if (!this.isInitialized) return;
    let val: string;
    if (!roadFilters) {
      val = 'unused';
    } else if (roadFilters.length === 0) {
      val = 'none selected';
    } else {
      val = roadFilters
        .map((el) => filterIdToRoadClass(el))
        .sort()
        .join(',');
    }

    this.heap.addEventProperties({
      road_classes_filters: val,
    });
  }
}

const heapAnalyticsService = new HeapAnalyticsService();

export function HeapAnalytics() {
  const user = useAppSelector(selectUser);
  const project = useAppSelector(selectProject);
  const mapStyle = useAppSelector(selectMapStyle);
  const roadFilters = useAppSelector(selectRoadFilters);
  heapAnalyticsService.initialize();

  useEffect(() => {
    heapAnalyticsService.setUserIdentify(user.email);
    heapAnalyticsService.setProjectProperty(project);
  }, [user, project]);

  useEffect(() => {
    heapAnalyticsService.setMapStyleEventProperty(mapStyle || kDefaultMapStyle);
  }, [mapStyle]);

  useEffect(() => {
    heapAnalyticsService.setRoadClassesEventProperty(roadFilters);
  }, [roadFilters]);

  return null;
}
